import { FormikContextType } from 'formik';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deduplicateStringsArray } from '../services/common/miscUtils';
import { FlowTypeEnum } from '../types/forms';
import { uiStateActions, uiStateSelectors } from '../services/redux/features/uiState.slice';

/**
 * - Provides title for the component/section/form under condition
 * that translation file has title information formatted in a certain way.
 * - See example translation formatting in customerDetailsForm.json under key 'title'.
 * @deprecated Please try switching to {@link getFormSectionTitle}
 */
const getSectionTitleForCustomer = (
  sectionTranslationKey: string,
  flowType: FlowTypeEnum,
  /**
   * There can be data of up to 2 people present in Customer Data forms
   * for business customers.
   * isMainApplicant means the title is for the first of those people.
   */
  isMainApplicant?: boolean
) => {
  let mainApplicantAffix = '';
  if (isMainApplicant !== undefined) {
    mainApplicantAffix = isMainApplicant ? '1' : '2';
  }

  if (flowType === FlowTypeEnum.consumer) {
    return t(`${sectionTranslationKey}:title:private${mainApplicantAffix}`);
  }

  return t(`${sectionTranslationKey}:title:${flowType}${mainApplicantAffix}`);
};

/**
 * - getSectionTitleForCustomer curried for simplicity.
 * @see {@link getSectionTitleForCustomer} for details.
 * @deprecated Please try switching to {@link getFormSectionTitle}
 */
export const getSectionTitleForCustomerCurried = (flowType: FlowTypeEnum) => {
  /**
   * - Provide `isMainApplicant` only when title for form in question
   * has 2 permutations for given customerType.
   */
  return (sectionTranslationKey: string, isMainApplicant?: boolean) => {
    return getSectionTitleForCustomer(sectionTranslationKey, flowType, isMainApplicant);
  };
};

type FormId = string;

/**
 * Scrolls to the top of a given section smoothly.
 */
const scrollToSectionTop = (formId: FormId): void => {
  setTimeout(
    () => {
      const currentSection = document.getElementById(formId);
      currentSection?.scrollIntoView();
    },
    /**
     * - Arbitrary timeout allowing previous focused section to collapse.
     * - Focusing when page geometry still changes will give faulty results.
     */
    650
  );
};

const getFirstNotSubmittedForm = (orderedFormsToRender: ReadonlyArray<FormId>, submittedForms: FormId[]) => {
  return orderedFormsToRender.find((formId) => !submittedForms.includes(formId));
};

export const useFormSectionHelpers = <T extends ReadonlyArray<FormId>>(orderedFormsToRender: T) => {
  const [submittedForms, setSubmittedForms] = useState<FormId[]>([]);
  const [expandedForm, setExpandedForm] = useState<FormId | undefined>(undefined);
  const [allFormsSubmitted, setAllFormsSubmitted] = useState(false);
  const [sectionInEditMode, setSectionInEditMode] = useState<FormId | undefined>(undefined);
  const [lastSubmittedValues, setLastSubmittedValues] = useState<Record<FormId, string>>({});

  const dispatch = useDispatch();

  const summaryMode = useSelector(uiStateSelectors.isSummaryMode);

  useEffect(() => {
    const allSubmitted = orderedFormsToRender.every((form) => submittedForms.includes(form));
    setAllFormsSubmitted(allSubmitted);
  }, [orderedFormsToRender, submittedForms]);

  useEffect(() => {
    dispatch(uiStateActions.setAllFormsSubmitted(allFormsSubmitted));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFormsSubmitted]);

  useEffect(() => {
    dispatch(uiStateActions.setIsAnySectionInEditMode(Boolean(sectionInEditMode)));
  }, [dispatch, sectionInEditMode]);

  useEffect(() => {
    return () => {
      dispatch(uiStateActions.setAllFormsSubmitted(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFormHelpers = useCallback(
    (formId: T[number]) => {
      const getDefaultOpen = () => {
        if (expandedForm === formId) return true;

        if (submittedForms.length === 0) {
          // Set as open if it's the first form in the group
          // return formGroups[formGroupType][0] === formName;
          return orderedFormsToRender[0] === formId;
        }
        if (getFirstNotSubmittedForm(orderedFormsToRender, submittedForms) !== formId) {
          return false;
        }

        return false;
      };

      const getOnChange = () => {
        return (open) => {
          setExpandedForm(open ? formId : undefined);
        };
      };

      const onFormSubmitSuccess = (formValues?: any) => {
        const nextSubmittedForms = deduplicateStringsArray([...submittedForms, formId]);
        setSubmittedForms(nextSubmittedForms);

        // Store the stringified form values as the last submitted state
        if (formValues !== undefined) {
          setLastSubmittedValues((prev) => ({
            ...prev,
            [formId]: JSON.stringify(formValues),
          }));
        }

        const firstNotSubmittedForm = getFirstNotSubmittedForm(orderedFormsToRender, nextSubmittedForms);

        if (summaryMode && sectionInEditMode === formId) {
          setSectionInEditMode(undefined);
        }

        setExpandedForm(firstNotSubmittedForm);

        if (firstNotSubmittedForm) {
          scrollToSectionTop(firstNotSubmittedForm);
        }
      };

      const onFormValuesChange = (formValues: unknown) => {
        const stringifiedValues = JSON.stringify(formValues);
        const lastSubmittedValue = lastSubmittedValues[formId];

        if (lastSubmittedValue) {
          setSubmittedForms((prevSubmittedForms) => {
            if (prevSubmittedForms.includes(formId) && lastSubmittedValue !== stringifiedValues) {
              return prevSubmittedForms.filter((_formId) => _formId !== formId);
            }

            if (!prevSubmittedForms.includes(formId) && lastSubmittedValue === stringifiedValues) {
              return deduplicateStringsArray([...prevSubmittedForms, formId]);
            }

            return prevSubmittedForms;
          });
        }
      };

      const onFormInitialValuesChange = async (validateForm: FormikContextType<any>['validateForm']) => {
        const errors = await validateForm();
        if (Object.values(errors).length > 0) {
          setSubmittedForms([...submittedForms.filter((_formId) => _formId !== formId)]);
        } else {
          setSubmittedForms(deduplicateStringsArray([...submittedForms, formId]));
        }

        const firstNotSubmittedForm = getFirstNotSubmittedForm(orderedFormsToRender, submittedForms);
        setExpandedForm(firstNotSubmittedForm);

        if (firstNotSubmittedForm) {
          scrollToSectionTop(firstNotSubmittedForm);
        }
      };

      const getFormStatus = (): 'success' | 'error' | undefined => {
        /* TODO: handle form section status indicator logic */
        // if (submittedForms.includes(formId)) return 'success';
        // if (invalidForms.includes(formId)) return 'error';
        return undefined;
      };

      const onStartEditInSummaryMode = () => {
        setSectionInEditMode(formId);
      };

      const isDisabledFormSummary = summaryMode && sectionInEditMode !== formId;

      return {
        formSectionProps: {
          id: formId,
          status: getFormStatus(),
          onChange: getOnChange(),
          defaultOpen: summaryMode ? true : getDefaultOpen(),
        },
        summaryMode,
        /**
         * - True if we are in summary mode but given form is not unlocked of editing.
         */
        isDisabledFormSummary,
        isEditing: sectionInEditMode === formId,
        isOtherFormInEdit: sectionInEditMode && sectionInEditMode !== formId,
        onFormSubmitSuccess,
        onFormValuesChange,
        onFormInitialValuesChange,
        onStartEditInSummaryMode,
      };
    },
    [orderedFormsToRender, submittedForms, expandedForm, sectionInEditMode, summaryMode, lastSubmittedValues]
  );

  return getFormHelpers;
};

/**
 * - Helpers are responsible for form section auto-focus and folding in and out.
 */
export type FormHelpers = ReturnType<ReturnType<typeof useFormSectionHelpers>>;
export type OnFormInitialValuesChange = FormHelpers['onFormInitialValuesChange'];
export type onFormValuesChange = FormHelpers['onFormValuesChange'];
